import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * PremiumButton - Primary CTA Button with shine effect
 */
const PremiumButton = ({
  children,
  onClick,
  variant = 'primary', // 'primary', 'secondary', 'outline', 'ghost'
  size = 'default', // 'sm', 'default', 'lg'
  icon,
  iconPosition = 'right',
  className = '',
  disabled = false,
  fullWidth = false,
  ...props
}) => {
  const baseStyles = `
    relative overflow-hidden
    inline-flex items-center justify-center gap-3
    font-medium transition-all duration-500
    disabled:opacity-50 disabled:cursor-not-allowed
    group
  `;

  const sizeStyles = {
    sm: 'px-5 py-2.5 text-sm',
    default: 'px-8 py-4 text-base',
    lg: 'px-10 py-5 text-lg',
  };

  const variantStyles = {
    primary: `
      bg-accent text-black
      hover:shadow-[0_0_30px_rgba(0,255,209,0.4)]
      active:scale-[0.98]
    `,
    secondary: `
      bg-white/5 text-white border border-white/10
      hover:bg-white hover:text-black hover:border-white
      hover:shadow-[0_0_30px_rgba(255,255,255,0.1)]
      active:scale-[0.98]
    `,
    outline: `
      bg-transparent text-accent border-2 border-accent
      hover:bg-accent hover:text-black
      hover:shadow-[0_0_30px_rgba(0,255,209,0.3)]
      active:scale-[0.98]
    `,
    ghost: `
      bg-transparent text-white/70
      hover:text-white hover:bg-white/5
      active:scale-[0.98]
    `,
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseStyles}
        ${sizeStyles[size]}
        ${variantStyles[variant]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      {...props}
    >
      {/* Shine effect overlay */}
      {variant === 'primary' && (
        <span className="absolute inset-0 overflow-hidden">
          <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
        </span>
      )}

      {/* Glow pulse effect for primary */}
      {variant === 'primary' && (
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
          <span className="absolute inset-0 bg-accent/20 blur-xl" />
        </span>
      )}

      {/* Border glow for outline */}
      {variant === 'outline' && (
        <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10">
          <span className="absolute inset-[-2px] bg-accent/30 blur-md" />
        </span>
      )}

      {/* Content */}
      <span className="relative z-10 flex items-center gap-3">
        {icon && iconPosition === 'left' && (
          <span className="group-hover:-translate-x-1 transition-transform duration-300">
            {icon}
          </span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
          <span className="group-hover:translate-x-1 transition-transform duration-300">
            {icon}
          </span>
        )}
        {!icon && variant === 'primary' && (
          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
        )}
      </span>

      {/* Bottom highlight line */}
      {(variant === 'primary' || variant === 'outline') && (
        <span className="absolute bottom-0 left-0 w-0 h-[2px] bg-white/50 group-hover:w-full transition-all duration-500" />
      )}
    </button>
  );
};

/**
 * GlowButton - Button with animated border glow
 */
const GlowButton = ({
  children,
  onClick,
  className = '',
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative group px-8 py-4 font-medium text-white
        overflow-hidden
        ${className}
      `}
      {...props}
    >
      {/* Animated gradient border */}
      <span className="absolute inset-0 bg-gradient-to-r from-accent via-violet-500 to-accent bg-[length:200%_100%] animate-gradient-x opacity-70 group-hover:opacity-100 transition-opacity duration-500" />
      
      {/* Inner background */}
      <span className="absolute inset-[2px] bg-black" />
      
      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
      </span>
    </button>
  );
};

/**
 * ShimmerButton - Button with continuous shimmer effect
 */
const ShimmerButton = ({
  children,
  onClick,
  variant = 'accent', // 'accent', 'white'
  className = '',
  ...props
}) => {
  const colors = {
    accent: {
      bg: 'bg-accent',
      text: 'text-black',
      shimmer: 'via-white/40',
    },
    white: {
      bg: 'bg-white',
      text: 'text-black',
      shimmer: 'via-black/10',
    },
  };

  return (
    <button
      onClick={onClick}
      className={`
        relative overflow-hidden px-8 py-4 font-medium
        ${colors[variant].bg} ${colors[variant].text}
        hover:shadow-[0_0_40px_rgba(0,255,209,0.5)]
        active:scale-[0.98] transition-all duration-300
        group
        ${className}
      `}
      {...props}
    >
      {/* Continuous shimmer */}
      <span className="absolute inset-0 overflow-hidden">
        <span 
          className={`absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent ${colors[variant].shimmer} to-transparent skew-x-12`}
        />
      </span>

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
      </span>
    </button>
  );
};

/**
 * MorphButton - Button that morphs on hover
 */
const MorphButton = ({
  children,
  onClick,
  className = '',
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative px-8 py-4 font-medium text-black bg-accent
        overflow-hidden group
        hover:rounded-2xl
        active:scale-[0.98]
        transition-all duration-500 ease-out
        ${className}
      `}
      {...props}
    >
      {/* Expanding circle on hover */}
      <span className="absolute top-1/2 left-1/2 w-0 h-0 bg-white/20 rounded-full group-hover:w-[300%] group-hover:h-[300%] -translate-x-1/2 -translate-y-1/2 transition-all duration-500 ease-out" />

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-2 group-hover:scale-110 transition-all duration-300" />
      </span>
    </button>
  );
};

/**
 * PulseButton - Button with pulse effect
 */
const PulseButton = ({
  children,
  onClick,
  className = '',
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative px-8 py-4 font-medium text-black bg-accent
        active:scale-[0.98] transition-transform duration-200
        group
        ${className}
      `}
      {...props}
    >
      {/* Pulse rings */}
      <span className="absolute inset-0 rounded-none">
        <span className="absolute inset-0 bg-accent opacity-0 group-hover:opacity-100 group-hover:animate-ping-slow" />
      </span>

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
      </span>
    </button>
  );
};

/**
 * GlassButton - Glassmorphism button
 */
const GlassButton = ({
  children,
  onClick,
  className = '',
  ...props
}) => {
  return (
    <button
      onClick={onClick}
      className={`
        relative px-8 py-4 font-medium text-white
        bg-white/5 backdrop-blur-md
        border border-white/10
        hover:bg-white/10 hover:border-white/20
        hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)]
        active:scale-[0.98]
        transition-all duration-300
        group
        ${className}
      `}
      {...props}
    >
      {/* Highlight on hover */}
      <span className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-white/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Content */}
      <span className="relative z-10 flex items-center justify-center gap-3">
        {children}
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
      </span>
    </button>
  );
};

export { 
  PremiumButton, 
  GlowButton, 
  ShimmerButton, 
  MorphButton, 
  PulseButton,
  GlassButton 
};
